import React, { useCallback, useEffect, useState } from 'react';
import './DistanceTolerance.css';

export const DISTANCE_MIN = 25;
export const DISTANCE_MAX = 1000;
export const DISTANCE_STEP = 25;
/** One notch past the max: the cap comes off entirely. The API reads 0 as uncapped. */
export const DISTANCE_ANY_POS = DISTANCE_MAX + DISTANCE_STEP;

const clamp = (n: number) => Math.min(Math.max(n, DISTANCE_MIN), DISTANCE_MAX);

/** Slider position for a mileage cap. Undefined rides at the configured default so
 *  the handle still sits somewhere truthful before the broker touches it. */
const toPos = (miles: number | undefined, fallback?: number | null): number => {
  if (miles === 0) return DISTANCE_ANY_POS;
  if (miles === undefined) return clamp(Math.round(fallback ?? DISTANCE_MAX));
  return clamp(miles);
};

interface Props {
  /** Mileage cap. `0` = any distance, `undefined` = fall back to the default. */
  value: number | undefined;
  onChange: (miles: number | undefined) => void;
  /** Cap in force while `value` is undefined, so the control can show what it means. */
  fallbackMiles?: number | null;
  /** Offered only when the unset state is reachable and differs from the current one. */
  onReset?: () => void;
  label: string;
  id: string;
  title?: string;
}

/**
 * Distance cap as a draggable slider plus a typed mileage box, replacing the
 * fixed-rung drop-down (Josh, 9/9). The rungs were the constraint: a buyer 260 mi
 * out was invisible at "250" and only reachable by jumping to 500.
 *
 * Fully controlled — it keeps no copy of the cap. An earlier draft mirrored `value`
 * locally and committed back on a debounce, which raced Reset and re-applied the
 * value being cleared. Callers debounce their own refetch instead.
 */
export const DistanceTolerance: React.FC<Props> = ({
  value, onChange, fallbackMiles, onReset, label, id, title,
}) => {
  // The only local state: an in-progress edit, so the box can be empty mid-edit
  // without that empty string reading as "no cap". Any accepted value -- typed,
  // dragged, or reset -- lands back here through `value`, which drops the draft.
  const [typed, setTyped] = useState<string | null>(null);
  useEffect(() => { setTyped(null); }, [value]);

  const onType = useCallback((raw: string) => {
    setTyped(raw);
    const n = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(n) || n < 0) return;
    onChange(n === 0 ? 0 : Math.max(1, Math.round(n)));
  }, [onChange]);

  const isAny = value === 0;
  const isDefault = value === undefined;

  const readout = isAny
    ? 'Any distance'
    : isDefault
      ? `Default${fallbackMiles ? ` · ${Math.round(fallbackMiles)} mi` : ''}`
      : `${value.toLocaleString()} mi`;

  return (
    <div className="dt-wrap" title={title}>
      <label className="dt-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        className="dt-slider"
        type="range"
        min={DISTANCE_MIN}
        max={DISTANCE_ANY_POS}
        step={DISTANCE_STEP}
        value={toPos(value, fallbackMiles)}
        onChange={(e) => {
          const p = Number(e.target.value);
          onChange(p >= DISTANCE_ANY_POS ? 0 : p);
        }}
      />
      <input
        className="dt-miles"
        type="number"
        min={0}
        step={DISTANCE_STEP}
        inputMode="numeric"
        aria-label={`${label} in miles`}
        placeholder={isAny ? 'Any' : isDefault && fallbackMiles ? String(Math.round(fallbackMiles)) : ''}
        value={typed ?? (isAny || isDefault ? '' : String(value))}
        onChange={(e) => onType(e.target.value)}
      />
      <span className="dt-readout">{readout}</span>
      {onReset && !isDefault && (
        <button type="button" className="dt-reset" onClick={onReset}>
          Reset
        </button>
      )}
    </div>
  );
};
